/**
 * Naming a new folder. A name a folder beside it has already ("acme" is "Acme") is said in
 * the dialog, with the ways on: open that one, or take a free name ("Acme (2)").
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { FolderOut, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { folderHref, freeName, rememberMade, useFolderIndex } from "../../lib/folders.ts";
import { ignore } from "../../lib/ignore.ts";
import { spaceApi } from "../../lib/paths.ts";
import { asApiError, bump } from "../../lib/resource.ts";
import { navigate } from "../../lib/router.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";

const NAME_TAKEN = "name_taken";

/** The name field, saying when the name is taken beside it (`taken`: that folder's id), with its ways on. */
function NameForm(p: {
  space: SpaceOut;
  /** Where the folder is (or goes): its siblings' names make the free one. */
  parent: string | null;
  initial: string;
  submit: string;
  onSubmit: (name: string) => Promise<void>;
  onCancel: () => void;
}) {
  const index = useFolderIndex(p.space);
  const [name, setName] = useState(p.initial);
  const [taken, setTaken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const siblings = (index.data?.rows ?? []).filter((f) => (f.under ?? null) === p.parent).map((f) => f.name);
  const run = async (n: string) => {
    setBusy(true);
    try {
      await p.onSubmit(n.trim());
    } catch (e) {
      const err = asApiError(e);
      if (err.code === NAME_TAKEN) {
        setTaken(typeof err.info["folder"] === "string" ? err.info["folder"] : "");
      } else {
        toast(err.message, "error");
      }
    } finally {
      setBusy(false);
    }
  };
  const free = freeName(name.trim(), [...siblings, name.trim()]);
  return (
    <Form
      class="flex flex-col gap-4"
      onSubmit={() => {
        run(name).catch(ignore);
      }}
    >
      <Field label={t("folders.name")}>
        <Input
          value={name}
          onValue={(v) => {
            setName(v);
            setTaken(null);
          }}
          required={true}
          maxLength={255}
          autofocus={true}
        />
      </Field>
      {taken === null ? null : (
        <div role="status" class="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p class="text-amber-950">{t("folders.taken", { name: name.trim() })}</p>
          <div class="flex flex-wrap gap-2">
            {taken ? (
              <Button size="sm" onClick={() => navigate(folderHref(p.space, taken))}>
                {t("folders.openExisting")}
              </Button>
            ) : null}
            <Button
              size="sm"
              onClick={() => {
                setName(free);
                setTaken(null);
                run(free).catch(ignore);
              }}
            >
              {t("folders.useFree", { name: free })}
            </Button>
          </div>
        </div>
      )}
      <DialogActions>
        <Button answer="cancel" onClick={p.onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy}>
          {p.submit}
        </Button>
      </DialogActions>
    </Form>
  );
}

/** A new folder's name, made in `parent` (none: the root) when sent; `onMade` gets it. */
export function NewFolderForm(p: {
  space: SpaceOut;
  parent: string | null;
  onCancel: () => void;
  onMade: (f: FolderOut) => void;
}) {
  return (
    <NameForm
      space={p.space}
      parent={p.parent}
      initial=""
      submit={t("common.create")}
      onCancel={p.onCancel}
      onSubmit={async (name) => {
        const f = await api.post<FolderOut>(spaceApi(p.space, "/folders"), { name, parent: p.parent });
        // Listed where it was made, whatever the filters there.
        rememberMade(f.id);
        bump();
        p.onMade(f);
      }}
    />
  );
}

/** "New folder": made in `parent` (none: the root), where it's then listed (an empty one isn't worth going into). */
export function NewFolderDialog(p: { space: SpaceOut; open: boolean; parent: string | null; onClose: () => void }) {
  return (
    <Dialog open={p.open} onClose={p.onClose} title={t("folders.new")}>
      {p.open ? (
        <NewFolderForm
          space={p.space}
          parent={p.parent}
          onCancel={p.onClose}
          onMade={(f) => {
            p.onClose();
            toast(t("folders.made", { name: f.name }));
          }}
        />
      ) : null}
    </Dialog>
  );
}
